import { afterRejectionStatuses } from '@/lib/constants/med-requests'
import type { ExtraTreatment, RemovedTreatment } from '@/lib/stores/medication-request'
import type { Medication, MedRequest } from '@/types/data'

/**
 * Pure computation of what an MPS approve must apply to the subscription. Every comparison is
 * hash-based: a card is "changed" exactly when its pick's catalogue hash differs from its
 * baseline's (`persistedMedication`), so an untouched card contributes nothing to the plan.
 *
 * The sync executes the plan in field order — removals, then additions, then one batched swap —
 * so a product removed and re-picked in the same session never trips the API's duplicate reject.
 */

/**
 * The hash MPS compares and mutates by: variation hash, product hash as fallback (same shape as
 * `orderedItem.id`). A sister variant of the same product therefore reads as a change.
 */
export const catalogueHashOf = (medication: Pick<Medication, 'productHash' | 'variantHash'> | null | undefined) =>
  medication?.variantHash || medication?.productHash || null

/** N of a `…#MPSMED#N` order number — medication orders count up, so a higher N is newer. */
const medicationOrderIterationOf = (row: Pick<MedRequest, 'orderNumber'>) =>
  Number(row.orderNumber?.match(/#MPSMED#(\d+)$/i)?.[1] ?? 0)

/**
 * The newest row of each subscription item (`mpsHash`), the request included: older medication orders
 * still carry a dose or product since changed (CD-374). Rejected/refunded rows are skipped.
 */
export const currentRowsByItem = (medRequest: MedRequest) => {
  const rejectionStages = afterRejectionStatuses.map(stage => stage.toLowerCase())
  const rows = new Map<string, MedRequest>()

  for (const row of [medRequest, ...(medRequest.mpsMedRequests ?? [])]) {
    if (!row.mpsHash || !row.orderedItem?.id || rejectionStages.includes(row.stage?.toLowerCase() ?? '')) continue

    const current = rows.get(row.mpsHash)
    if (!current || medicationOrderIterationOf(row) > medicationOrderIterationOf(current)) rows.set(row.mpsHash, row)
  }

  return rows
}

/** Both hashes a family row can carry for a medication — a product-level row still belongs to its variant. */
const lookupHashesOf = (medication: Pick<Medication, 'productHash' | 'variantHash'>) =>
  [medication.variantHash, medication.productHash].filter((hash): hash is string => !!hash)

/** The family rows carrying any of the hashes, over the request plus its family. */
const familyRowsFor = (medRequest: MedRequest, hashes: string[]) => {
  const family = [medRequest, ...(medRequest.mpsMedRequests ?? [])]
  return family.filter(entry => entry.orderedItem?.id && hashes.includes(entry.orderedItem.id))
}

// The subscription order's item for a medication, resolved over the request plus its family.
// `mpsHash` (attached by the API — the 'mps' order's own item for the product) is the hash the
// swap/remove endpoints expect; a matching 'mps' row's id means the same thing. A medication
// order's own id lives in another hash space (it is a med-request id, which the API 400s), so
// data carrying neither resolves to null rather than to a guess.
export const findSubscriptionItemHash = (medRequest: MedRequest, hashes: string[]) => {
  const matches = familyRowsFor(medRequest, hashes)

  return (
    matches.map(entry => entry.mpsHash).find(Boolean) ??
    matches.find(entry => entry.evaroType?.toLowerCase() === 'mps')?.id ??
    null
  )
}

/**
 * The medication-order row carrying a product — its id is the med request id the /update
 * endpoint takes afterwards ('mps' mirror rows are not actionable med requests).
 */
const findMedOrderRequestId = (medRequest: MedRequest, hashes: Array<string | null | undefined>) => {
  const wanted = hashes.filter(Boolean)
  const family = [medRequest, ...(medRequest.mpsMedRequests ?? [])]

  return (
    family.find(
      entry =>
        entry.evaroType?.toLowerCase() !== 'mps' && entry.orderedItem?.id && wanted.includes(entry.orderedItem.id)
    )?.id ?? null
  )
}

/**
 * Resolves a card baseline to the subscription item it represents, most reliable source first:
 * the item hash captured at seed time, then the family row whose order is the one the add
 * response reported (unique per medication — the mirrored 'mps' rows all share the
 * subscription's hash, so only a medication order matches; its hash then leads back to the
 * mirrored subscription item, the hash swap/remove expect), then the medication's own hashes as
 * a last resort. Null means the item can't be located (yet) — e.g. the refetch after a
 * partially failed approve hasn't landed.
 */
const resolveBaselineItemHash = (
  medRequest: MedRequest,
  baseline: { itemHash?: string | null; orderHash?: string | null; medication?: Medication | null }
) => {
  if (baseline.itemHash) return baseline.itemHash

  if (baseline.orderHash) {
    const row = (medRequest.mpsMedRequests ?? []).find(entry => entry.orderHash === baseline.orderHash)
    if (row?.mpsHash) return row.mpsHash
    if (row?.orderedItem?.id) {
      const viaRow = findSubscriptionItemHash(medRequest, [row.orderedItem.id])
      if (viaRow) return viaRow
    }
  }

  return baseline.medication ? findSubscriptionItemHash(medRequest, lookupHashesOf(baseline.medication)) : null
}

export type MpsSyncPlan = {
  /** Queued removals resolved to a subscription item; executed first. */
  removals: Array<{ itemHash: string; removed: RemovedTreatment }>
  /** Queued removals whose product is no longer on the family — already gone; only unqueued. */
  settledRemovals: RemovedTreatment[]
  /**
   * Adds, one call each — an extra card's un-persisted pick by catalogue hash. `treatmentId`
   * ties the add back to its card so the landed baseline can be recorded.
   */
  additions: Array<{ catalogueHash: string; treatmentId: string; medication: Medication }>
  /**
   * All swaps go out in one batched call. `itemHash` is the subscription order's own item
   * (`mpsHash`), `newCatalogueHash` its replacement; `medReqId` the medication-order row the
   * /update goes to afterwards (card's persisted id as fallback). `treatmentId` as for
   * additions; null marks Treatment 1.
   */
  swaps: Array<{
    itemHash: string
    newCatalogueHash: string
    treatmentId: string | null
    medReqId: string | null
    medication: Medication
  }>
  /**
   * Changed cards and queued removals whose product is still on the family but whose
   * subscription item can't be located — the sync must not run with these.
   */
  unresolvedBaselines: Array<ExtraTreatment | RemovedTreatment>
  /** A changed Treatment 1 whose subscription item can't be located — aborts the sync too. */
  unresolvedPrimarySwap: boolean
}

export const computeMpsSyncPlan = (
  medRequest: MedRequest,
  picks: {
    medication: Medication | null
    extraTreatments: ExtraTreatment[]
    removedTreatments: RemovedTreatment[]
  }
): MpsSyncPlan => {
  const { medication, extraTreatments, removedTreatments } = picks
  const plan: MpsSyncPlan = {
    removals: [],
    settledRemovals: [],
    additions: [],
    swaps: [],
    unresolvedBaselines: [],
    unresolvedPrimarySwap: false
  }

  for (const removed of removedTreatments) {
    const itemHash = resolveBaselineItemHash(medRequest, removed)

    // No item hash with the product still on the family is unresolved, not settled — treating
    // it as settled would silently unqueue the removal and leave the product on the subscription.
    if (itemHash) plan.removals.push({ itemHash, removed })
    else if (familyRowsFor(medRequest, lookupHashesOf(removed.medication)).length)
      plan.unresolvedBaselines.push(removed)
    else plan.settledRemovals.push(removed)
  }

  // Treatment 1 is the request's own order item. Exact hash equality: re-picking the current
  // variation is a no-op, narrowing a product-level item to one of its variations is a swap.
  const primaryHash = catalogueHashOf(medication)
  const currentItemId = medRequest.orderedItem?.id
  const isPrimaryUnchanged = !!currentItemId && primaryHash === currentItemId

  if (medication && primaryHash && !isPrimaryUnchanged) {
    // Exact match on each item's newest row. On Treatment 1's own item only a newer order's row can match —
    // a swap that already landed (a retry). A product queued for removal above doesn't count.
    const removedHashes = new Set(removedTreatments.flatMap(entry => lookupHashesOf(entry.medication)))
    const pickAlreadyOnSubscription =
      !removedHashes.has(primaryHash) &&
      [...currentRowsByItem(medRequest).values()].some(row => row.orderedItem.id === primaryHash)

    if (pickAlreadyOnSubscription) {
      // The picked variation is already a subscription item — nothing to change.
    } else {
      // `mpsHash` — the subscription order's own item for the medication — is the hash swap
      // expects, with the family's annotations and mirror rows as fallback; the request's own
      // id is a med-request id in another hash space, so an unresolvable item aborts the sync
      // rather than sending a hash the API will 400. The placeholder plan product, which PHP
      // refuses to swap, never reaches the frontend, so a changed Treatment 1 always swaps in
      // place.
      const primaryItemHash =
        medRequest.mpsHash ?? (currentItemId ? findSubscriptionItemHash(medRequest, [currentItemId]) : null)

      if (primaryItemHash) {
        plan.swaps.push({
          itemHash: primaryItemHash,
          newCatalogueHash: primaryHash,
          treatmentId: null,
          medReqId: findMedOrderRequestId(medRequest, [currentItemId]),
          medication
        })
      } else {
        plan.unresolvedPrimarySwap = true
      }
    }
  }

  for (const treatment of extraTreatments) {
    const nextMedication = treatment.medication
    const catalogueHash = catalogueHashOf(nextMedication)
    if (!nextMedication || !catalogueHash) continue

    if (!treatment.persistedMedication) {
      plan.additions.push({ catalogueHash, treatmentId: treatment.id, medication: nextMedication })
    } else if (catalogueHashOf(treatment.persistedMedication) !== catalogueHash) {
      const itemHash = resolveBaselineItemHash(medRequest, {
        itemHash: treatment.itemHash,
        orderHash: treatment.persistedOrderHash,
        medication: treatment.persistedMedication
      })

      if (itemHash) {
        plan.swaps.push({
          itemHash,
          newCatalogueHash: catalogueHash,
          treatmentId: treatment.id,
          medReqId:
            findMedOrderRequestId(medRequest, lookupHashesOf(treatment.persistedMedication)) ??
            treatment.persistedMedReqId ??
            null,
          medication: nextMedication
        })
      } else {
        plan.unresolvedBaselines.push(treatment)
      }
    }
  }

  return plan
}
